import { useState, useEffect } from 'react';
import { Send, Plus, Trash2, VolumeX, Volume2 } from 'lucide-react';
import ModuleContainer from '../layout/ModuleContainer.jsx';

const CATEGORIES = ['macro', 'crypto', 'crypto_small_cap_calls', 'geopolitics', 'news', 'voices'];

export default function TelegramChannelsSection() {
  const [channels, setChannels] = useState([]);
  const [adding, setAdding] = useState(false);
  const [newChannel, setNewChannel] = useState({ handle: '', category: 'macro', notes: '' });
  const [error, setError] = useState(null);

  async function load() {
    try {
      const r = await fetch('/api/telegram/channels');
      if (r.ok) setChannels(await r.json());
    } catch {}
  }
  useEffect(() => { load(); }, []);

  async function addChannel() {
    setError(null);
    if (!newChannel.handle) { setError('Handle required'); return; }
    try {
      const r = await fetch('/api/telegram/channels', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newChannel),
      });
      const data = await r.json();
      if (!r.ok) { setError(data.error || 'Failed to add'); return; }
      setNewChannel({ handle: '', category: 'macro', notes: '' });
      setAdding(false);
      load();
    } catch (e) { setError(e.message); }
  }

  async function updateStatus(handle, status) {
    await fetch(`/api/telegram/channels/${encodeURIComponent(handle)}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status }),
    });
    load();
  }

  async function removeChannel(handle) {
    if (!confirm(`Remove ${handle}? (soft-delete, can be restored)`)) return;
    await fetch(`/api/telegram/channels/${encodeURIComponent(handle)}`, { method: 'DELETE' });
    load();
  }

  const active = channels.filter(c => c.status === 'active' || c.enabled === 1);
  const muted = channels.filter(c => c.status === 'muted');
  const removed = channels.filter(c => c.status === 'removed');

  return (
    <ModuleContainer
      title={<span className="flex items-center gap-2"><Send className="w-4 h-4" /> Telegram Channels</span>}
      subtitle={`${active.length} active · ${muted.length} muted · ${removed.length} removed`}
    >
      <div className="space-y-3">
        {/* Add form */}
        {adding ? (
          <div className="bg-korinth-bg rounded p-3 space-y-2">
            <div className="flex gap-2 items-end">
              <div className="flex-1">
                <label className="text-[9px] font-mono text-korinth-muted">Handle (e.g. @BossmanCallsOfficial)</label>
                <input value={newChannel.handle} onChange={e => setNewChannel(n => ({ ...n, handle: e.target.value }))}
                  className="block w-full bg-korinth-surface border border-korinth-border rounded px-2 py-1 text-xs font-mono text-white" placeholder="@channelname" />
              </div>
              <div>
                <label className="text-[9px] font-mono text-korinth-muted">Category</label>
                <select value={newChannel.category} onChange={e => setNewChannel(n => ({ ...n, category: e.target.value }))}
                  className="block bg-korinth-surface border border-korinth-border rounded px-2 py-1 text-xs font-mono text-white">
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>
            <input value={newChannel.notes} onChange={e => setNewChannel(n => ({ ...n, notes: e.target.value }))}
              className="block w-full bg-korinth-surface border border-korinth-border rounded px-2 py-1 text-[10px] font-mono text-white" placeholder="Notes (optional)" />
            {error && <div className="text-[10px] text-korinth-danger font-mono">{error}</div>}
            <div className="flex gap-2">
              <button onClick={addChannel} className="flex items-center gap-1 text-[10px] font-mono px-3 py-1 rounded bg-korinth-accent/10 text-korinth-accent border border-korinth-accent/30 hover:bg-korinth-accent/20">
                <Plus className="w-3 h-3" /> Add Channel
              </button>
              <button onClick={() => { setAdding(false); setError(null); }} className="text-[10px] font-mono px-3 py-1 rounded text-korinth-muted hover:text-korinth-text">Cancel</button>
            </div>
          </div>
        ) : (
          <button onClick={() => setAdding(true)} className="flex items-center gap-1 text-[10px] font-mono px-3 py-1 rounded bg-korinth-accent/10 text-korinth-accent border border-korinth-accent/30 hover:bg-korinth-accent/20">
            <Plus className="w-3 h-3" /> Add Channel
          </button>
        )}

        {/* Channels table */}
        <table className="w-full text-[10px] font-mono">
          <thead>
            <tr className="text-korinth-muted border-b border-korinth-border/30">
              <th className="text-left py-1 px-2">Handle</th>
              <th className="text-left py-1 px-2">Category</th>
              <th className="text-left py-1 px-2">Status</th>
              <th className="text-right py-1 px-2">Trust</th>
              <th className="text-right py-1 px-2">Msgs</th>
              <th className="py-1 px-2"></th>
            </tr>
          </thead>
          <tbody>
            {channels.filter(c => c.status !== 'removed').map(c => {
              const handle = c.handle;
              const isActive = c.status === 'active' || (!c.status && c.enabled === 1);
              const trustColor = c.trust_score >= 60 ? '#10b981' : c.trust_score >= 40 ? '#f59e0b' : '#ef4444';
              return (
                <tr key={handle} className="border-b border-korinth-border/10 hover:bg-korinth-bg/30">
                  <td className="py-1.5 px-2">
                    <a href={`https://t.me/${handle.replace('@', '')}`} target="_blank" rel="noopener noreferrer" className="text-white font-bold hover:text-korinth-accent">
                      {handle}
                    </a>
                  </td>
                  <td className="py-1.5 px-2 text-korinth-muted">{c.category || '—'}</td>
                  <td className="py-1.5 px-2">
                    <span className={`text-[8px] px-1.5 py-0.5 rounded font-bold uppercase ${isActive ? 'bg-korinth-accent/10 text-korinth-accent' : 'bg-korinth-muted/10 text-korinth-muted'}`}>
                      {c.status || (isActive ? 'active' : 'inactive')}
                    </span>
                  </td>
                  <td className="py-1.5 px-2 text-right font-bold" style={{ color: trustColor }}>{c.trust_score}</td>
                  <td className="py-1.5 px-2 text-right text-korinth-muted">{c.total_messages || 0}</td>
                  <td className="py-1.5 px-2 flex gap-1 justify-end">
                    {isActive ? (
                      <button onClick={() => updateStatus(handle, 'muted')} className="text-korinth-muted hover:text-korinth-warning" title="Mute"><VolumeX className="w-3 h-3" /></button>
                    ) : (
                      <button onClick={() => updateStatus(handle, 'active')} className="text-korinth-muted hover:text-korinth-accent" title="Unmute"><Volume2 className="w-3 h-3" /></button>
                    )}
                    <button onClick={() => removeChannel(handle)} className="text-korinth-danger/60 hover:text-korinth-danger" title="Remove"><Trash2 className="w-3 h-3" /></button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </ModuleContainer>
  );
}
